<a [routerLink]="['/caller-tracking']" class="back-link mb-3 d-inline-block">
	<i class="ri-arrow-left-line"></i> Back to Caller Tracking
</a>

<div *ngIf="loading" class="text-center text-muted py-5">
	<div class="spinner-border spinner-border-sm me-2"></div> Loading caller data…
</div>

<div *ngIf="!loading && notFound" class="alert alert-warning">
	Caller not found — they may no longer be online. Try again from the Caller Tracking list.
</div>

<ng-container *ngIf="!loading && caller">
	<!-- Header -->
	<div class="card mb-4">
		<div class="card-body d-flex justify-content-between align-items-center flex-wrap gap-2">
			<div class="d-flex align-items-center gap-3">
				<div class="ct-avatar">{{ initials(caller.full_name) }}</div>
				<div>
					<h5 class="mb-0">{{ caller.full_name }}</h5>
					<small class="text-muted">
						{{ caller.portal === "CAL" ? "Caller" : caller.portal === "ADM" ? "Admin" : "Customer" }}
						· Logged in {{ caller.signin_time | utcToLocal: "shortTime" }}
					</small>
				</div>
			</div>
			<div class="d-flex align-items-center" [ngClass]="statusClass">
				<span class="idle-circle"></span>
				<span class="fw-semibold">{{ statusLabel }}</span>
				<small class="text-muted ms-2">· {{ caller.date_time | dateAgo }}</small>
			</div>
		</div>
	</div>

	<!-- KPI tiles -->
	<div class="row mb-4">
		<div class="col-md-2 col-6">
			<div class="card card-animate card-blue">
				<div class="card-body">
					<p class="text-muted mb-0">Dials</p>
					<h4>{{ kpi.dials || "—" }}</h4>
					<small class="text-muted">today</small>
				</div>
			</div>
		</div>
		<div class="col-md-2 col-6">
			<div class="card card-animate card-green">
				<div class="card-body">
					<p class="text-muted mb-0">Talk Time</p>
					<h4>{{ talk(kpi.talkMs) }}</h4>
					<small class="text-muted">today</small>
				</div>
			</div>
		</div>
		<div class="col-md-2 col-6">
			<div class="card card-animate card-orange">
				<div class="card-body">
					<p class="text-muted mb-0">Dispos</p>
					<h4>{{ kpi.dispos || "—" }}</h4>
					<small class="text-muted">{{ kpi.disposRate | number: "1.0-0" }}% of dials</small>
				</div>
			</div>
		</div>
		<div class="col-md-2 col-6">
			<div class="card card-animate card-green">
				<div class="card-body">
					<p class="text-muted mb-0">Leads Set</p>
					<h4>{{ kpi.leads || "—" }}</h4>
					<small class="text-muted">{{ kpi.dispos ? (kpi.leads / kpi.dispos * 100 | number: "1.0-1") : 0 }}% conv</small>
				</div>
			</div>
		</div>
		<div class="col-md-2 col-6">
			<div class="card card-animate card-purple">
				<div class="card-body">
					<p class="text-muted mb-0">Campaigns</p>
					<h4>{{ kpi.campaigns || "—" }}</h4>
					<small class="text-muted">worked today</small>
				</div>
			</div>
		</div>
		<div class="col-md-2 col-6">
			<div class="card card-animate card-red">
				<div class="card-body">
					<p class="text-muted mb-0">Idle Time</p>
					<h4 [ngClass]="statusClass">{{ caller.date_time | dateAgo }}</h4>
					<small class="text-muted">{{ statusLabel }}</small>
				</div>
			</div>
		</div>
	</div>

	<div class="row mb-4">
		<!-- Dispos per hour -->
		<div class="col-lg-7">
			<div class="card h-100">
				<div class="card-header">
					<h6 class="mb-0">Dispos Per Hour</h6>
					<small class="text-muted">Logged dispos by hour — proxy for call activity (raw dial timestamps aren't tracked per caller)</small>
				</div>
				<div class="card-body">
					<apx-chart
						*ngIf="hourBuckets.length; else noHourly"
						[series]="hourChart.series"
						[chart]="{ height: 260, type: 'bar', toolbar: { show: false } }"
						[xaxis]="{ categories: hourChart.categories }"
						[colors]="['#40A0FC', '#50E7A6']"
						[dataLabels]="{ enabled: false }"
						[legend]="{ position: 'top', horizontalAlign: 'right' }"
					></apx-chart>
					<ng-template #noHourly>
						<div class="text-muted text-center py-4">No dispos logged yet today.</div>
					</ng-template>
				</div>
			</div>
		</div>

		<!-- Dispo breakdown -->
		<div class="col-lg-5">
			<div class="card h-100">
				<div class="card-header d-flex justify-content-between align-items-center">
					<h6 class="mb-0">Dispo Breakdown</h6>
					<span class="badge bg-light text-dark">{{ kpi.dispos }} total</span>
				</div>
				<div class="card-body">
					<div *ngFor="let b of dispoBreakdown" class="ct-dispo-row">
						<div class="d-flex justify-content-between">
							<span>{{ b.label }}</span>
							<span class="text-muted">{{ b.count }} ({{ b.percent | number: "1.0-0" }}%)</span>
						</div>
						<div class="progress" style="height: 6px">
							<div class="progress-bar" [style.width.%]="b.percent" [style.background-color]="b.color"></div>
						</div>
					</div>
					<div *ngIf="!kpi.dispos" class="text-muted text-center py-3">No dispos logged yet today.</div>
				</div>
			</div>
		</div>
	</div>

	<!-- Campaign breakdown -->
	<div class="card mb-4">
		<div class="card-header">
			<h6 class="mb-0">Campaign Breakdown</h6>
		</div>
		<div class="card-body p-0">
			<div class="table-responsive">
				<table class="table table-striped mb-0">
					<thead class="table-light">
						<tr>
							<th>Campaign</th>
							<th>Dispos</th>
							<th>Leads</th>
							<th>Conv %</th>
						</tr>
					</thead>
					<tbody>
						<tr *ngFor="let c of campaignBreakdown">
							<td>{{ c.campaign }}</td>
							<td>{{ c.dispos }}</td>
							<td>{{ c.leads }}</td>
							<td>{{ c.convPct | number: "1.0-1" }}%</td>
						</tr>
						<tr *ngIf="!campaignBreakdown.length">
							<td colspan="4" class="text-center text-muted py-3">No campaigns worked yet today.</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	</div>

	<!-- Activity timeline -->
	<div class="card mb-4">
		<div class="card-header">
			<h6 class="mb-0">Activity Timeline</h6>
			<small class="text-muted">Most recent first</small>
		</div>
		<div class="card-body p-0">
			<ul class="list-group list-group-flush">
				<li class="list-group-item d-flex justify-content-between align-items-center" *ngFor="let a of timeline">
					<div>
						<span [class]="'badge ' + badgeClass(a.dispo)">{{ a.dispo }}</span>
						<strong class="ms-2">{{ a.prospect_name }}</strong>
						<span class="text-muted"> · {{ a.campaign_name }}</span>
					</div>
					<small class="text-muted">{{ a.date | utcToLocal: "short" }}</small>
				</li>
				<li *ngIf="!timeline.length" class="list-group-item text-center text-muted py-3">No activity logged yet today.</li>
			</ul>
		</div>
	</div>
</ng-container>
